<template>
  <div class="organization-billing-requests mt-5" v-if="organization">

    <v-tabs v-model="activeTab" class="mb-6" centered>
      <v-tab>{{ $tc('billing-request.billing-request', 2) }}</v-tab>
      <v-tab>{{ $tc('billing-request.pre-invoicing', 2) }}</v-tab>
      <v-tab>{{ $tc('invoice.invoice', 2) }}</v-tab>
    </v-tabs>

    <!-- Tab 1: Demandes de facturation -->
    <v-tabs-items v-model="activeTab">
      <v-tab-item>
        <v-card>
          <v-card-text>
            <v-row dense>
              <v-col>
                <date-picker label="Date de création" v-model="br.rangeDate" clearable hide-details range />
              </v-col>
              <v-col>
                <date-picker label="Date de la période" v-model="br.rangePeriodDate" clearable hide-details range />
              </v-col>
              <v-col cols="5">
                <v-select :label="$t('status')" multiple small-chips hide-details deletable-chips clearable :items="brStatues" v-model="br.filters.statues" />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-divider class="my-4" />

        <v-data-table
            class="br-table"
            :headers="brHeaders"
            v-model="br.selected"
            :items="br.items"
            :options.sync="br.options"
            :loading="br.loading"
            :server-items-length="br.total"
            :footer-props="{ 'items-per-page-options': [20, 50, 100] }"
            show-select
        >
          <template #item.createdAt="{item}">{{ item.createdAt | formatDate($t('format_date')) }}</template>
          <template #item.startDate="{item}">{{ formatUtcDate(item.startDate, $t('format_date')) }} - {{ formatUtcDate(item.endDate, $t('format_date')) }}</template>
          <template #item.netTotal="{item}">{{ item.netTotal ? numberFormat(item.netTotal) + ' €' : '-' }}</template>
          <template #item.status="{item}">
            {{ $t('billing-request.statues.' + item.status) }}
            <v-tooltip bottom v-if="item.treatedExternally">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" color="warning" small class="ml-2">mdi-briefcase-check</v-icon>
              </template>
              <span>{{ $t('billing-request.treated-externally-tooltip') }}</span>
            </v-tooltip>
          </template>
          <template #item.options="{item}">
            <v-btn icon small @click="openBrModal(item.id)"><v-icon>mdi-eye-outline</v-icon></v-btn>
            <v-btn class="ml-3" v-if="item.billingRequestInvoice" icon small @click="openBriPage(item.billingRequestInvoice._id)"><v-icon>mdi-file</v-icon></v-btn>
          </template>
          <template #footer.prepend v-if="currentUser.isZE">
            <v-btn text small @click="downloadBrCsv"><v-icon left>mdi-download</v-icon>{{ $t('export') }}</v-btn>
          </template>
        </v-data-table>

        <template v-if="br.selected.length > 0">
          <v-row class="mt-3">
            <v-col>{{ $tc('selected-items', br.selected.length, { count: br.selected.length }) }}</v-col>
          </v-row>
          <v-row>
            <v-col class="d-flex flex-wrap">
              <v-chip class="mr-2 mb-1" small v-for="s in br.selected" :key="s.id" close @click:close="br.selected = br.selected.filter(i => i.id !== s.id)">{{ s.billingRequestReference }}</v-chip>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-btn small @click="$refs.createBriModal.open()"><v-icon left>mdi-send</v-icon>{{ $t('billing-request.prepare-invoice') }}</v-btn>
            </v-col>
          </v-row>
        </template>

        <create-billing-request-invoice-modal ref="createBriModal" :selectedItems="br.selected" />
        <billing-request-show-modal ref="brModal" @updated="loadBr" />
      </v-tab-item>

      <!-- Tab 2: Pré-facturation -->
      <v-tab-item>
        <v-card>
          <v-card-text>
            <v-row dense>
              <v-col>
                <date-picker label="Date de création" v-model="pre.rangeDate" clearable hide-details range />
              </v-col>
              <v-col cols="5">
                <v-select :label="$t('status')" multiple clearable small-chips deletable-chips hide-details :items="preStatues" v-model="pre.filters.statues" />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-divider class="my-4" />

        <v-data-table
            :headers="preHeaders"
            :items="pre.items"
            :options.sync="pre.options"
            :loading="pre.loading"
            :server-items-length="pre.total"
        >
          <template #item.createdAt="{item}">{{ item.createdAt | formatDate($t('format_date')) }}</template>
          <template #item.netExceptedAmount="{item}">{{ item.netExceptedAmount ? numberFormat(item.netExceptedAmount) + ' €' : '-' }}</template>
          <template #item.status="{item}">{{ $t('billing-request-invoice.statues.' + item.status) }}</template>
          <template #item.options="{item}">
            <v-btn icon small :to="{name: 'billing-requests.invoices.show', params: {id: item.id}}"><v-icon>mdi-eye-outline</v-icon></v-btn>
          </template>
        </v-data-table>
      </v-tab-item>

      <!-- Tab 3: Factures -->
      <v-tab-item>
        <v-card>
          <v-card-text>
            <v-row dense>
              <v-col>
                <date-picker label="Date de création" v-model="inv.rangeDate" clearable hide-details range />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-divider class="my-4" />

        <v-data-table
            :headers="invHeaders"
            :items="inv.items"
            :options.sync="inv.options"
            :loading="inv.loading"
            :server-items-length="inv.total"
        >
          <template #item.createdAt="{item}">{{ item.createdAt | formatDate($t('format_date')) }}</template>
          <template #item.netExceptedAmount="{item}">{{ item.netExceptedAmount ? numberFormat(item.netExceptedAmount) + ' €' : '-' }}</template>
          <template #item.status="{item}">{{ $t('billing-request-invoice.statues.' + item.status) }}</template>
          <template #item.options="{item}">
            <v-btn icon small :to="{name: 'billing-requests.invoices.show', params: {id: item.id}}"><v-icon>mdi-eye-outline</v-icon></v-btn>
          </template>
        </v-data-table>
      </v-tab-item>
    </v-tabs-items>

  </div>
</template>

<script>
import BillingRequestRepository from "@repository/BillingRequestRepository";
import BillingRequestShowModal from "@pages/BillingRequest/modal/BillingRequestShowModal.vue";
import CreateBillingRequestInvoiceModal from "@pages/BillingRequest/modal/CreateBillingRequestInvoiceModal.vue";

export default {
  components: { BillingRequestShowModal, CreateBillingRequestInvoiceModal },

  data() {
    return {
      activeTab: 0,
      // Tab 1: Billing Requests
      br: {
        loading: false, selected: [], rangeDate: [], rangePeriodDate: [],
        filters: { statues: ['ready-to-invoice', 'preparing', 'waiting-for-review'] },
        total: 0, items: [], options: null,
      },
      // Tab 2: Pre-invoicing
      pre: {
        loading: false, rangeDate: [],
        filters: { statues: ['waiting-for-review', 'draft'] },
        total: 0, items: [], options: null,
      },
      // Tab 3: Invoices (billing request invoices)
      inv: {
        loading: false, rangeDate: [],
        total: 0, items: [], options: null,
      },
    }
  },

  watch: {
    'br.filters': { handler() { this.resetAndLoad('br') }, deep: true },
    'br.rangeDate'() { this.loadBr() },
    'br.rangePeriodDate'() { this.loadBr() },
    'br.options'() { this.loadBr() },

    'pre.filters': { handler() { this.resetAndLoad('pre') }, deep: true },
    'pre.rangeDate'() { this.loadPre() },
    'pre.options'() { this.loadPre() },

    'inv.rangeDate'() { this.loadInv() },
    'inv.options'() { this.loadInv() },
  },

  computed: {
    organization() { return this.$parent.organization ?? null },

    brHeaders() {
      return [
        { text: this.$t('billing-request.reference'), value: 'billingRequestReference' },
        { text: this.$t('period'), value: 'startDate' },
        { text: this.$t('name'), value: 'issuerName' },
        { text: this.$t('price-ht'), value: 'netTotal' },
        { text: this.$t('status'), value: 'status' },
        { text: this.$t('created-at'), value: 'createdAt' },
        { value: 'options' },
      ]
    },

    preHeaders() {
      return [
        { text: 'Ref. BRI', value: 'billingRequestInvoiceReference' },
        { text: this.$t('billing-request.reference'), value: 'reference' },
        { text: this.$t('price-ht'), value: 'netExceptedAmount' },
        { text: this.$t('status'), value: 'status' },
        { text: this.$t('created-at'), value: 'createdAt' },
        { value: 'options' },
      ]
    },

    invHeaders() {
      return [
        { text: 'Ref. BRI', value: 'billingRequestInvoiceReference' },
        { text: this.$t('billing-request.reference'), value: 'reference' },
        { text: this.$t('price-ht'), value: 'netExceptedAmount' },
        { text: this.$t('status'), value: 'status' },
        { text: this.$t('created-at'), value: 'createdAt' },
        { value: 'options' },
      ]
    },

    brStatues() {
      return [
        { text: this.$t('billing-request.statues.in-generation'), value: 'in-generation' },
        { text: this.$t('billing-request.statues.ready-to-invoice'), value: 'ready-to-invoice' },
        { text: this.$t('billing-request.statues.preparing'), value: 'preparing' },
        { text: this.$t('billing-request.statues.waiting-for-review'), value: 'waiting-for-review' },
        { text: this.$t('billing-request.statues.treated'), value: 'treated' },
        { text: this.$t('billing-request-invoice.statues.canceled'), value: 'canceled' },
      ]
    },

    preStatues() {
      return [
        { text: this.$t('billing-request-invoice.statues.draft'), value: 'draft' },
        { text: this.$t('billing-request-invoice.statues.waiting-for-review'), value: 'waiting-for-review' },
        { text: this.$t('billing-request-invoice.statues.ready'), value: 'ready' },
        { text: this.$t('billing-request-invoice.statues.submitted'), value: 'submitted' },
        { text: this.$t('billing-request-invoice.statues.canceled'), value: 'canceled' },
      ]
    },
  },

  methods: {
    resetAndLoad(tab) {
      if (this[tab].options?.page > 1) {
        this[tab].options.page = 1
      } else {
        if (tab === 'br') this.loadBr()
        if (tab === 'pre') this.loadPre()
      }
    },

    buildPagination(tab) {
      const opts = this[tab].options
      if (!opts) return null
      const data = {}
      if (opts.itemsPerPage > -1) {
        data.offset = (opts.page * opts.itemsPerPage) - opts.itemsPerPage
      }
      data.limit = opts.itemsPerPage
      if (opts.sortBy?.length > 0) data.sortBy = opts.sortBy[0]
      if (opts.sortDesc?.length > 0) data.sortOrder = opts.sortDesc[0] ? 'desc' : 'asc'
      return data
    },

    loadBr() {
      if (!this.organization || !this.br.options) return
      this.br.loading = true
      const data = { organizationId: this.organization.id, ...this.br.filters, ...this.buildPagination('br') }
      if (this.br.rangeDate?.length > 0) { data.startDate = this.br.rangeDate[0]; data.endDate = this.br.rangeDate[1] }
      if (this.br.rangePeriodDate?.length > 0) { data.startPeriodDate = this.br.rangePeriodDate[0]; data.endPeriodDate = this.br.rangePeriodDate[1] }

      BillingRequestRepository.search(data).then(response => {
        this.br.total = response.total
        this.br.items = response.items.map(item => ({
          ...item,
          isSelectable: item.status !== 'canceled' && (!item.billingRequestInvoice || ['canceled', 'rejected'].includes(item.billingRequestInvoice.status)),
        }))
        this.br.loading = false
      }).catch(error => {
        this.notifyError(error)
        this.br.loading = false
      })
    },

    loadPre() {
      if (!this.organization || !this.pre.options) return
      this.pre.loading = true
      const data = { organizationId: this.organization.id, ...this.pre.filters, ...this.buildPagination('pre') }
      if (this.pre.rangeDate?.length > 0) { data.startDate = this.pre.rangeDate[0]; data.endDate = this.pre.rangeDate[1] }

      BillingRequestRepository.searchInvoices(data).then(response => {
        this.pre.total = response.total
        this.pre.items = response.items
        this.pre.loading = false
      }).catch(error => {
        this.notifyError(error)
        this.pre.loading = false
      })
    },

    loadInv() {
      if (!this.organization || !this.inv.options) return
      this.inv.loading = true
      const data = { organizationId: this.organization.id, statues: ['submitted'], ...this.buildPagination('inv') }
      if (this.inv.rangeDate?.length > 0) { data.startDate = this.inv.rangeDate[0]; data.endDate = this.inv.rangeDate[1] }

      BillingRequestRepository.searchInvoices(data).then(response => {
        this.inv.total = response.total
        this.inv.items = response.items
        this.inv.loading = false
      }).catch(error => {
        this.notifyError(error)
        this.inv.loading = false
      })
    },

    openBrModal(id) { this.$refs.brModal.open(id) },
    openBriPage(id) { this.$router.push({ name: 'billing-requests.invoices.show', params: { id } }) },

    downloadBrCsv() {
      const data = { organizationId: this.organization.id, ...this.br.filters }
      if (this.br.rangeDate?.length > 0) { data.startDate = this.br.rangeDate[0]; data.endDate = this.br.rangeDate[1] }
      if (this.br.rangePeriodDate?.length > 0) { data.startPeriodDate = this.br.rangePeriodDate[0]; data.endPeriodDate = this.br.rangePeriodDate[1] }
      this.showLoading(true)
      BillingRequestRepository.exportCsv(data).then(() => { this.showLoading(false) }).catch(err => { this.notifyError(err); this.showLoading(false) })
    },
  },
}
</script>
